import { notification } from 'antd';
import { useQueryClient } from 'react-query';
import { useSearchParams } from 'react-router-dom';

import useAPIMutation from '../api/useAPIMutation';
import useSearchCandidatesController from '../hooks/useSearchCandidatesController';
import { JobPostingDetail } from '../types';
import { buildRecruitmentProcessQueryParam } from '../utils';
import { EmailFormValues } from './EmailForm';
import SendAdditionalQuestionsModal, {
  SendAdditionalQuestionsModalProps,
} from './SendAdditionalQuestionsModal';

interface BulkSendAdditionalQuestionsModalProps
  extends Omit<SendAdditionalQuestionsModalProps, 'helmetTitle' | 'hasCandidatesThatAnswered'> {
  jobPostingId?: JobPostingDetail['id'];
  onSuccess?: (data: void, variables: EmailFormValues, context: unknown) => void | Promise<unknown>;
  functionId?: JobPostingDetail['function_id'];
  totalCount?: number;
}

const BulkSendAdditionalQuestionsModal = ({
  onSuccess,
  jobPostingId,
  totalCount,
  functionId,
  ...props
}: BulkSendAdditionalQuestionsModalProps) => {
  const queryClient = useQueryClient();

  const [searchParams] = useSearchParams();

  const finalizedAdditionalQuestionsSearchCandidates = useSearchCandidatesController({
    key: 'finalized-bulk-send-additional-questions',
    enabled: false,
    jobPostingId,
    functionId: functionId || undefined,
    page: 1,
    pageSize: 1,
    additionalQuestionsCompleteness: 'FINALIZED',
  });

  const mutation = useAPIMutation(
    async (
      { message, subject, attachments, enableReplyTo, fromName, replyTo }: EmailFormValues,
      api
    ) => {
      const attachmentIds: number[] = [];

      for (const attachment of attachments || []) {
        if (attachment.originFileObj) {
          const attachmentFormData = new FormData();
          attachmentFormData.append('filename', attachment.name);
          attachmentFormData.append('file', attachment.originFileObj as File, attachment.name);
          attachmentFormData.append('mimetype', attachment.type as string);
          const attachmentResponse = await api.post('/attachments/', attachmentFormData, {
            headers: {
              'Content-Type': 'multipart/form-data',
            },
          });

          attachmentIds.push(attachmentResponse.data.id);
        } else {
          attachmentIds.push(parseInt(attachment.uid, 10));
        }
      }

      const previousAdditionalQuestionsCompletenessParam = searchParams.get(
        'additionalQuestionsCompleteness'
      );

      // Temporarily setting additionalQuestionsCompleteness Param to MISSING when filter is empty
      // to only send emails to candidates that didn't answer the additional questions
      if (!previousAdditionalQuestionsCompletenessParam) {
        searchParams.set('additionalQuestionsCompleteness', 'MISSING');
      }

      if (searchParams.get('additionalQuestionsCompleteness') === 'MISSING') {
        await api.post(
          '/recruitment-processes-actions/send-email-to-all/',
          {
            from_name: fromName,
            job_posting: jobPostingId,
            subject,
            message,
            ...(enableReplyTo ? { reply_to: replyTo } : {}),
            type: 'ADDITIONAL_QUESTIONS',
            attachments: attachmentIds,
          },
          {
            params: await buildRecruitmentProcessQueryParam(
              searchParams,
              api,
              jobPostingId,
              functionId
            ),
          }
        );
      }

      // Return searchParams to original state
      if (previousAdditionalQuestionsCompletenessParam) {
        searchParams.set(
          'additionalQuestionsCompleteness',
          previousAdditionalQuestionsCompletenessParam
        );
      } else {
        searchParams.delete('additionalQuestionsCompleteness');
      }
    },
    {
      onSuccess,
      onSettled: async () => {
        await queryClient.invalidateQueries('/candidate-history/');
      },
      onError: (err, variables, context) => {
        if (err?.response?.data?.error === 'empty_queryset_send_email_to_all') {
          return notification.error({
            duration: 0,
            message: 'Erro',
            description:
              'Não é possível realizar esta ação, pois não há candidatos a serem selecionados. Recarregue a página e tente novamente.',
          });
        }

        return queryClient.defaultMutationOptions().onError?.(err, variables, context);
      },
    }
  );

  const hasCandidatesThatAnswered = async () => {
    if (searchParams.get('additionalQuestionsCompleteness') === 'MISSING') {
      return false;
    }

    const finalizedAdditionalQuestions =
      await finalizedAdditionalQuestionsSearchCandidates.refetch();

    if (finalizedAdditionalQuestions.data && finalizedAdditionalQuestions.data.count > 0) {
      return true;
    }
    return false;
  };

  return (
    <SendAdditionalQuestionsModal
      title={
        <span>
          Enviar perguntas de pré-requisito para <strong>{totalCount} candidato(s)</strong>
        </span>
      }
      helmetTitle={`Enviar perguntas de pré-requisito para ${totalCount} candidato(s)`}
      hasCandidatesThatAnswered={hasCandidatesThatAnswered}
      loading={mutation.isLoading}
      onFinish={(values) => {
        mutation.mutate(values);
      }}
      jobPostingId={jobPostingId}
      {...props}
    />
  );
};

export default BulkSendAdditionalQuestionsModal;
